import { Link } from 'react-router-dom'
import { useSidTitel } from '../lib/seo'

/** Analys av skillnaderna mellan Gy11:s kursplan (LEDLED0) och Gy25:s
 *  ämnesplan (LEDA) — riktad till lärare som undervisat sedan innan Gy25 och
 *  söker på just den övergången. Texten är hand­författad (jämför de två
 *  ordagranna kursplanetexterna på /kursplan och /kursplan/gy11) och ska
 *  hållas saklig och källnära: varje påstående om en skillnad går att
 *  verifiera direkt mot de två kursplanstexterna. */
export default function KursplanAndringar() {
  useSidTitel('Från Gy11 till Gy25 — vad har ändrats i kursplanen?')
  return (
    <div className="wrap">
      <article className="dokument">
        <h1>Från Gy11 till Gy25: vad har hänt med kursplanen i Ledarskap och organisation?</h1>

        <p>
          Undervisar du sedan innan 2025 känner du redan igen det mesta i ämnet ledarskap
          och organisation. Kärnan är i stort sett orörd mellan Gy11 och Gy25: samma
          tvärvetenskapliga bas i företagsekonomi, psykologi, sociologi och pedagogik,
          samma centrala teman kring individ, grupp, ledarskap och organisation. Den
          verkliga förändringen ligger inte i vad kursen handlar om, utan i vad eleven
          bedöms på.
        </p>

        <h2>Bedömningen tappar sin praktiska del</h2>
        <p>
          Gy11:s betygskriterier för E krävde mer än beskrivningar. Sista stycket löd:
          ”Eleven använder i samråd med handledare relevanta metoder för att leda och
          organisera en verksamhet i bekanta situationer. När eleven samråder med
          handledare bedömer hon eller han med viss säkerhet den egna förmågan och
          situationens krav.” Det är en praktisk komponent: eleven skulle faktiskt leda
          något tillsammans med en handledare och sedan bedöma sin egen insats.
        </p>
        <p>
          Den komponenten finns inte kvar i Gy25. Betygskriterierna för E stannar vid att
          beskriva, resonera och analysera: ”Eleven beskriver på ett godtagbart sätt
          innebörden av begrepp, teorier och modeller … Eleven gör i huvudsak fungerande
          analyser av frågor inom ledarskap och organisation med hjälp av teorier och
          modeller.” Varken handledare, samråd eller självskattning av den egna förmågan
          nämns. Samma mönster går igen i syftestexten: Gy11 ville att eleverna skulle
          utveckla ”tillit till sin egen ledarskapsförmåga” och en uttalad förmåga att
          leda och organisera en verksamhet. Ingen av formuleringarna finns kvar i Gy25.
        </p>
        <p>
          Kort sagt: Gy25 prövar om eleven förstår och kan resonera kring ledarskap och
          organisation. Gy11 prövade dessutom om eleven kunde utöva det, med en
          handledare som bollplank. Det förklarar också varför det här läromedlets
          övningar och prov är uppbyggda kring teorier, modeller och analys snarare än
          handledd praktik i en verksamhet.
        </p>

        <h2>Ämnesbeskrivningen: nästan ordagrant densamma</h2>
        <p>
          Själva definitionen av ämnet har knappt ändrats. Gy11 beskrev ämnet som
          tvärvetenskapligt, med bas i företagsekonomi, psykologi, sociologi och
          pedagogik, ”ur såväl ekonomiska som sociala och etiska aspekter”. Gy25 använder
          samma mening, ord för ord, förutom att den sista bisatsen om ekonomiska, sociala
          och etiska aspekter är struken. Känner du igen ämnet från Gy11 känner du igen
          det i Gy25 också.
        </p>

        <h2>Syftet: samma riktning, en ny ton</h2>
        <p>
          Syftestexten i Gy25 håller fast vid det mesta från Gy11: individers och
          gruppers sätt att fungera, organisationsformer, organisationskultur, ett
          kritiskt och analyserande förhållningssätt, fältstudier. En mening är dock ny:
          att undervisningen ska ”stimulera elevernas kunskapssökande, kreativitet och
          vilja att omsätta idéer i praktisk handling” — den formuleringen fanns inte i
          Gy11.
        </p>
        <p>
          Listan över vad eleverna ska utveckla förutsättningar för krymper samtidigt
          från sju punkter i Gy11 till fyra i Gy25. Det mesta av innehållet lever kvar i
          det centrala innehållet ändå, men en punkt försvinner helt och hänger ihop med
          skiftet ovan: en uttalad förmåga att leda och organisera en verksamhet finns
          varken i syftestexten eller i det centrala innehållet i Gy25.
        </p>

        <h2>Centralt innehåll: sju punkter blir elva</h2>
        <p>
          Gy11 samlade det centrala innehållet i sju punkter. Gy25 delar upp samma stoff
          i elva, mer avgränsade punkter. Grupprocesser, grupputveckling och
          konflikthantering, som Gy11 slog samman i en enda punkt, blir tre separata
          punkter i Gy25. Etik, demokrati och mångfald, som Gy11 samlade i en punkt om
          ”etiskt och historiskt perspektiv samt … demokrati och genus”, blir två
          punkter i Gy25: en om etisk medvetenhet och demokratiska värderingar, en om
          jämställdhet, mångfald och diskriminering.
        </p>
        <p>
          Den tydligaste nyheten är en helt egen punkt: ”Analys av frågor om ledarskap
          och organisation med utgångspunkt i teorier och modeller … Reflektion över
          nyheter och tendenser inom området.” Något liknande fanns bara som en
          syftesformulering i Gy11, aldrig som en egen punkt i det centrala innehållet. I
          det här läromedlet är det den punkten som ligger bakom{' '}
          <Link to="/kapitel/11-aktuella-tendenser-och-analys">
            kapitel elva, Aktuella tendenser och analys
          </Link>
          .
        </p>

        <h2>En ny beteckning: kurs blir nivå</h2>
        <p>
          En sista skillnad är mer administrativ än innehållslig. Gy11 hade en
          fristående kurs, ”Ledarskap och organisation, 100 poäng”, med kurskoden
          LEDLED0. Gy25 kallar samma omfattning för ”Nivå 1, 100 poäng”, med nivåkoden
          LEDA1000X — ett generellt mönster för hela Gy25-reformen, inte något unikt för
          det här ämnet. Poängomfattningen är densamma; bara ordet och koden har bytts
          ut.
        </p>

        <h2>Vad betyder det för din undervisning?</h2>
        <p>
          Har du lektionsmaterial eller bedömningsunderlag kvar från Gy11 går det mesta
          att återanvända rakt av — begreppen, teorierna och modellerna är i stort sett
          desamma. Det som inte längre efterfrågas i betygskriterierna är den handledda
          praktiken: att eleven faktiskt leder en verksamhet tillsammans med en
          handledare och sedan bedömer sin egen insats. Vill du ändå ha kvar ett sådant
          moment i din undervisning går det förstås bra — det strider inte mot Gy25 —
          men det är inte längre något kursplanen kräver för något betyg.
        </p>

        <p className="kalla-not">
          Jämförelsen bygger på en direkt läsning av de två kursplanstexterna: den{' '}
          <Link to="/kursplan">gällande ämnesplanen (Gy25, LEDA)</Link> och den{' '}
          <Link to="/kursplan/gy11">arkiverade kursplanen (Gy11, LEDLED0)</Link>, båda
          hämtade i sin helhet från Skolverket. Analysen är läromedlets egen och är inte
          en del av någon kursplan.
        </p>
      </article>
    </div>
  )
}
